/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  InputNumber,
  Calendar,
  message,
} from 'antd';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { checkToken } from 'api/utils';
import { onError } from 'utils/handlers';
import {
  fechasName,
  ModelVacacionesRegistrosFechas,
  numeroDiasName,
  urlAgregarVacacionesFechas,
  getFechasOcupadasPorGuardia,
  getFechasOcupadasPorVacacion,
} from 'api/nominas/vacaciones/vacacionesEmpleadosDiasDisfrutadosFechas';
import moment from 'moment';

const VacacionesRegistrosFechas = ({
  permission, parentId, formParent, dates, disableFields, empleado,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState();
  const [open, setVisible] = useState();
  const [visibleAlert, setVisibleAlert] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const form = formParent;
  const vigenciaInicial = Form.useWatch('fecha_inicial_vigencia_vacaciones', form);
  const vigenciaFinal = Form.useWatch('fecha_final_vigencia_vacaciones', form);

  const [selectedDates, setSelectedDates] = useState([]); // moment[]
  const [calendarValue, setCalendarValue] = useState(dates?.length
    ? moment(dates[0]?.id)
    : moment());
  const [calendarMode, setCalendarMode] = useState('month'); // 'month' | 'year'

  // Días que el empleado ya tiene registrados como Guardia: un mismo día no
  // puede ser a la vez Vacación disfrutada y Guardia, así que se bloquean en
  // este calendario (ver getFechasOcupadasPorGuardia).
  const [fechasOcupadasPorGuardia, setFechasOcupadasPorGuardia] = useState([]);

  // Días ya capturados en OTRO registro de Vacación disfrutada de este mismo
  // empleado (se excluye `parentId`, el registro que se está editando, para no
  // bloquear sus propias fechas ya guardadas). Ahora que un empleado puede tener
  // varios registros con el mismo Año de Vacaciones + Periodo Vacacional, dos
  // registros no pueden compartir la misma fecha.
  const [fechasOcupadasPorOtraVacacion, setFechasOcupadasPorOtraVacacion] = useState([]);

  useEffect(() => {
    let mounted = true;
    if (!empleado) {
      setFechasOcupadasPorGuardia([]);
      setFechasOcupadasPorOtraVacacion([]);
      return undefined;
    }
    getFechasOcupadasPorGuardia(empleado).then((_fechas) => {
      if (mounted) setFechasOcupadasPorGuardia(_fechas || []);
    });
    getFechasOcupadasPorVacacion(empleado, parentId).then((_fechas) => {
      if (mounted) setFechasOcupadasPorOtraVacacion(_fechas || []);
    });
    return () => { mounted = false; };
  }, [empleado, parentId]);

  const { cliente, nameModel, nameModelSingular } = ModelVacacionesRegistrosFechas;

  const formInstances = [form];

  const fetchData = async () => {
    setLoading(true);
    const _data = await cliente.obtenerListaDeRegistros(
      { vacacion_empleado_dia_disfrutado: parentId },
    );
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const _data = await cliente.obtenerListaDeRegistros(
        { vacacion_empleado_dia_disfrutado: parentId },
      );
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setCalendarValue(dates?.length ? moment(dates[0]?.id) : moment());
    setVisible(false);
  };

  const onClickAdd = () => {
    onCancel();
    const fechas = data.map((value) => value?.fecha_registro);
    form.setFieldValue(fechasName, fechas);
    setSelectedDates(fechas?.map((fecha) => moment(fecha)));
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields([fechasName]);
      const fechas = form.getFieldValue(fechasName) || [];
      const diasConDerecho = form.getFieldValue('dias_con_derecho');
      if (diasConDerecho != null && fechas.length > diasConDerecho) {
        message.warn(
          `No se pueden capturar ${fechas.length} día(s): el empleado solo tiene `
          + `${diasConDerecho} día(s) pendiente(s) disponibles para este periodo.`,
        );
        setLoading(false);
        return;
      }
      const payloadFechas = fechas.map((_fecha) => ({ fecha: _fecha }));
      const urlAgregar = urlAgregarVacacionesFechas;
      const saved = await cliente.reemplazarRegistro(parentId, payloadFechas, urlAgregar);
      if (saved) {
        // El backend recalcula fecha_inicial/fecha_final del registro como min/max de los días
        // guardados; se refleja en el formulario para que un "Guardar" posterior del padre no
        // los pise con el rango anterior.
        if (fechas.length) {
          const ordenadas = [...fechas].sort();
          form.setFieldsValue({
            fecha_inicial: moment(ordenadas[0]),
            fecha_final: moment(ordenadas[ordenadas.length - 1]),
          });
        }
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await cliente.borrarRegistro(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Fecha',
      dataIndex: 'fecha_registro',
      key: 'fecha_registro',
      width: 250,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  const onSelectDate = (date) => {
    // El Calendar de antd también llama a onSelect al cambiar de mes/año desde su encabezado
    // (con el mismo día del mes, en otro mes), al elegir un mes en la vista "Año" y al hacer
    // clic en un día de un mes adyacente. Eso es solo navegación: no debe marcar ni desmarcar
    // ningún día. Solo cuenta el clic en un día del mes que se está mostrando.
    if (calendarMode !== 'month' || !date.isSame(calendarValue, 'month')) return;
    setSelectedDates((prev) => {
      const exists = prev.some((d) => d.isSame(date, 'day'));
      const next = exists
        ? prev.filter((d) => !d.isSame(date, 'day'))
        : [...prev, date];

      const fechas = next.map((d) => d.format('YYYY-MM-DD'));
      form.setFieldValue(fechasName, fechas);
      form.setFieldValue(numeroDiasName, next.length);

      return next;
    });
  };

  const dateCellRender = (value) => {
    const cellDate = moment(value);
    const isSelected = selectedDates.some((d) => d.isSame(cellDate, 'day'));
    const isOcupadaPorGuardia = fechasOcupadasPorGuardia.includes(cellDate.format('YYYY-MM-DD'));
    const isOcupadaPorOtraVacacion = fechasOcupadasPorOtraVacacion.includes(cellDate.format('YYYY-MM-DD'));
    if (isOcupadaPorGuardia) {
      return (
        <div
          title="Ya registrado como día de Guardia"
          style={{
            height: '100%',
            backgroundColor: '#52c41a33',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
          }}
        >
          {value.date()}
        </div>
      );
    }
    if (isOcupadaPorOtraVacacion) {
      return (
        <div
          title="Ya registrado como vacación disfrutada en otro registro"
          style={{
            height: '100%',
            backgroundColor: '#ff4d4f33',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
          }}
        >
          {value.date()}
        </div>
      );
    }
    return isSelected ? (
      <div
        title="Vacación disfrutada"
        style={{
          height: '100%',
          backgroundColor: '#1890ff33',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
        }}
      >
        {value.date()}
      </div>
    ) : null;
  };

  // Las vacaciones se pueden tomar cualquier día del año, incluyendo fines de semana
  // (a diferencia de las Guardias, que solo aplican en días hábiles). Si el registro
  // tiene Periodo Vacacional, además deben caer dentro de su Vigencia de Vacaciones;
  // si no lo tiene (la mayoría de los registros existentes) no hay otro límite.
  // No se usa el `fecha_inicial`/`fecha_final` del registro: el backend los recalcula como
  // min/max de los días capturados, así que acotarse a ellos impedía ampliar el rango.
  const disabledDate = (current) => {
    if (!current) return true;
    if (fechasOcupadasPorGuardia.includes(current.format('YYYY-MM-DD'))) return true;
    if (fechasOcupadasPorOtraVacacion.includes(current.format('YYYY-MM-DD'))) return true;
    if (vigenciaInicial && vigenciaFinal) {
      return current.isBefore(moment(vigenciaInicial), 'day')
        || current.isAfter(moment(vigenciaFinal), 'day');
    }
    return false;
  };

  const fechasCapturadasDataSource = selectedDates.map((d) => ({
    id: d.format('YYYY-MM-DD'),
    fecha: d,
  }));

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickDelete,
          }}
          permission={permission}
          disableDelete={disableFields}
          allowExportParam={false}
          allowSearch={false}
        />
        <Modal
          open={open}
          closable={false}
          destroyOnClose={false}
          maskClosable={!loading}
          footer={null}
          width="50%"
          title={(
            <FormSubmitControls
              label={`Capturar ${nameModel}`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="fechasVacaciones"
            disabled={disableFields}
          >
            <Col xs={24} md={16}>
              <Calendar
                fullscreen={false}
                value={calendarValue}
                onSelect={onSelectDate}
                onPanelChange={(val, mode) => {
                  setCalendarValue(val);
                  setCalendarMode(mode);
                }}
                dateCellRender={dateCellRender}
                disabledDate={disableFields ? () => true : disabledDate}
              />
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  marginTop: 8,
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      backgroundColor: '#1890ff66',
                    }}
                  />
                  <span style={{ fontSize: 12 }}>Vacaciones Disfrutadas</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      backgroundColor: '#52c41a66',
                    }}
                  />
                  <span style={{ fontSize: 12 }}>Días de Guardia</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      backgroundColor: '#ff4d4f66',
                    }}
                  />
                  <span style={{ fontSize: 12 }}>Vacación en Otro Registro</span>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={24}>
              <Form.Item
                name={fechasName}
                label="Fechas Capturadas"
              >
                <Select
                  mode="multiple"
                  dataSource={fechasCapturadasDataSource}
                  render={(value) => value?.fecha?.format('DD-MM-YYYY')}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name={numeroDiasName}
                label="No. de Días"
                rules={rules.required}
                hasFeedback
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`${nameModelSingular} ${selectedRowKeys[0] ?? ''}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default VacacionesRegistrosFechas;
